<template>
  <div v-if="isReady" class="customer-service-wrapper">
    <button v-if="!panelVisible" class="customer-service-fab" @click="openPanel" :aria-label="$t('common.在线客服')">
      <svg class="fab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M4 12a8 8 0 0 1 16 0"/>
        <path d="M4 12v3.2A1.8 1.8 0 0 0 5.8 17H7v-5H4zM20 12v3.2A1.8 1.8 0 0 1 18.2 17H17v-5h3z"/>
        <path d="M8 17v1.2A2.8 2.8 0 0 0 10.8 21h2.4A2.8 2.8 0 0 0 16 18.2V17"/>
      </svg>
      <span v-if="unreadCount > 0" class="fab-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <div v-if="panelVisible" class="customer-service-panel">
      <div class="panel-header">
        <div class="header-left">
          <img class="avatar-logo" src="@/assets/images/plenty-logo.png" :alt="$t('common.品牌名')" />
          <div>
            <div class="title">{{ $t('common.在线客服') }}</div>
            <div class="status"><span class="online-dot"></span>{{ $t('common.在线') }}</div>
          </div>
        </div>
        <div class="header-actions">
          <button class="icon-btn close" @click="closePanel">×</button>
        </div>
      </div>

      <div ref="messageListRef" class="panel-body">
        <div v-for="msg in messages" :key="msg.id" class="message-row" :class="msg.role">
          <div class="bubble" :class="{ image: msg.type === 'image' }">
            <img v-if="msg.type === 'image'" :src="msg.imageUrl" alt="" class="chat-image" />
            <span v-else>{{ msg.isWelcome ? $t('common.客服欢迎语') : msg.text }}</span>
          </div>
          <div class="time">{{ formatTime(msg.time) }}</div>
        </div>
      </div>

      <div class="panel-footer">
        <input ref="uploadInputRef" type="file" accept="image/*" class="upload-input" @change="onUploadImage" />
        <button class="upload-btn" @click="triggerUpload">+</button>
        <input
          v-model="draftMessage"
          class="message-input"
          type="text"
          :placeholder="$t('common.输入消息占位')"
          @keyup.enter="sendMessage"
        />
        <button class="send-btn" @click="sendMessage">{{ $t('common.发送') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from '@/services/api.js'
import Pusher from 'pusher-js'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'CustomerServiceButton',
  data() {
    return {
      isReady: true,
      panelVisible: false,
      draftMessage: '',
      messages: [],
      maxUploadSize: 5 * 1024 * 1024,
      sending: false,
      lastMessageId: null,
      pusher: null,
      channel: null,
      userId: null,
      pollTimer: null,
      unreadCount: 0,
      unreadTimer: null
    }
  },
  mounted() {
    this.checkUnread()
    this.unreadTimer = setInterval(() => this.checkUnread(), 10000)
  },
  beforeUnmount() {
    this.disconnectSocket()
    this.stopPolling()
    if (this.unreadTimer) { clearInterval(this.unreadTimer); this.unreadTimer = null }
  },
  methods: {
    async checkUnread() {
      if (this.panelVisible) return
      if (!localStorage.getItem('wallet_token')) return
      try {
        const lastReadId = localStorage.getItem('cs_last_read_id') || '0'
        const res = await request(`/customer-service/unread-count?lastReadId=${lastReadId}`)
        if (res && res.success) this.unreadCount = res.data.count || 0
      } catch (e) { /* ignore */ }
    },
    markAsRead() {
      if (this.lastMessageId) {
        localStorage.setItem('cs_last_read_id', String(this.lastMessageId))
      }
      this.unreadCount = 0
    },
    async openPanel() {
      this.panelVisible = true
      await this.loadConversation()
      this.markAsRead()
      this.connectSocket()
      this.startPolling()
    },
    closePanel() {
      this.markAsRead()
      this.panelVisible = false
      this.disconnectSocket()
      this.stopPolling()
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => this.pollNewMessages(), 3000)
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    },
    async pollNewMessages() {
      if (!this.panelVisible) return
      try {
        const res = await request('/customer-service/conversation')
        if (!res.success) return
        const mapped = this.mapMessages(res.data.messages)
        const lastKnown = this.lastMessageId
        const newMsgs = lastKnown
          ? mapped.filter(m => m.id > lastKnown)
          : []
        if (newMsgs.length > 0) {
          for (const nm of newMsgs) {
            if (!this.messages.some(m => m.id === nm.id)) {
              this.messages.push(nm)
            }
          }
          this.lastMessageId = mapped[mapped.length - 1].id
          this.markAsRead()
          this.$nextTick(this.scrollToBottom)
        }
      } catch (e) { /* ignore */ }
    },
    /** 将后端消息格式转为前端展示格式 */
    mapMessages(apiMessages) {
      return (apiMessages || []).map(msg => ({
        id: msg.id,
        role: msg.sender_type === 'user' ? 'user' : 'support',
        type: msg.type || 'text',
        text: msg.type === 'image' ? '' : msg.content,
        imageUrl: msg.type === 'image' ? msg.content : '',
        time: new Date(msg.created_at).getTime()
      }))
    },
    /** 加载对话和历史消息 */
    async loadConversation() {
      try {
        const res = await request('/customer-service/conversation')
        if (res.success) {
          const mapped = this.mapMessages(res.data.messages)
          const welcomeTime = mapped.length ? mapped[0].time - 1000 : Date.now()
          this.messages = [
            { id: 'welcome', role: 'support', type: 'text', isWelcome: true, time: welcomeTime },
            ...mapped
          ]
          if (mapped.length) {
            this.lastMessageId = mapped[mapped.length - 1].id
          }
          this.$nextTick(this.scrollToBottom)
        }
      } catch (e) {
        console.error('加载客服对话失败:', e)
        this.messages = [{ id: 'welcome', role: 'support', type: 'text', isWelcome: true, time: Date.now() }]
      }
    },
    /** 发送文字消息到后端 */
    async sendMessage() {
      const content = String(this.draftMessage || '').trim()
      if (!content || this.sending) return
      this.sending = true

      // 先乐观地显示在界面上
      const tempId = 'temp_' + Date.now()
      this.messages.push({ id: tempId, role: 'user', type: 'text', text: content, time: Date.now() })
      this.draftMessage = ''
      this.$nextTick(this.scrollToBottom)

      try {
        const socketId = this.pusher?.connection?.socket_id
        const res = await request('/customer-service/send', {
          method: 'POST',
          body: JSON.stringify({ content, type: 'text' }),
          headers: socketId ? { 'X-Socket-ID': socketId } : {}
        })
        if (res.success && res.data) {
          const idx = this.messages.findIndex(m => m.id === tempId)
          if (idx !== -1) {
            this.messages[idx].id = res.data.id
            this.messages[idx].time = new Date(res.data.created_at).getTime()
            this.lastMessageId = res.data.id
          }
        }
      } catch (e) {
        console.error('发送消息失败:', e)
        // 发送失败则移除临时消息
        const idx = this.messages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.messages.splice(idx, 1)
      }
      this.sending = false
    },
    triggerUpload() {
      const uploadInput = this.$refs.uploadInputRef
      if (uploadInput) uploadInput.click()
    },
    /** 上传图片并发送图片消息 */
    async onUploadImage(event) {
      const input = event.target
      const file = input && input.files && input.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) { input.value = ''; return }
      if (file.size > this.maxUploadSize) { input.value = ''; return }
      input.value = ''

      try {
        // 第1步: 上传图片文件
        const formData = new FormData()
        formData.append('image', file)
        const uploadRes = await request('/customer-service/upload-image', {
          method: 'POST',
          body: formData
        })
        if (uploadRes.success) {
          const imageUrl = uploadRes.data.url
          // 第2步: 发送图片消息
          const socketId = this.pusher?.connection?.socket_id
          const sendRes = await request('/customer-service/send', {
            method: 'POST',
            body: JSON.stringify({ content: imageUrl, type: 'image' }),
            headers: socketId ? { 'X-Socket-ID': socketId } : {}
          })
          if (sendRes.success && sendRes.data) {
            this.messages.push({
              id: sendRes.data.id, role: 'user', type: 'image', imageUrl, time: new Date(sendRes.data.created_at).getTime()
            })
            this.lastMessageId = sendRes.data.id
            this.$nextTick(this.scrollToBottom)
          }
        }
      } catch (e) {
        console.error('上传图片失败:', e)
      }
    },
    /** 连接 WebSocket（Pusher） */
    connectSocket() {
      if (this.pusher) return // 已连接

      // 获取用户 ID
      try {
        const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
        this.userId = userInfo.id
      } catch (e) { /* ignore */ }

      if (!this.userId) {
        console.warn('客服 WebSocket: 无法获取用户 ID，跳过连接')
        return
      }

      const walletAddress = localStorage.getItem('wallet_address') ||
                            localStorage.getItem('address') ||
                            sessionStorage.getItem('address') || ''
      const token = localStorage.getItem('wallet_token') || ''

      const pusherKey = window.__PUSHER_APP_KEY__ || process.env.VUE_APP_PUSHER_APP_KEY || 'app-key'
      const pusherHost = window.__PUSHER_HOST__ || process.env.VUE_APP_PUSHER_HOST || new URL(API_BASE_URL).hostname
      const pusherPort = window.__PUSHER_PORT__ || process.env.VUE_APP_PUSHER_PORT || 6001
      const pusherScheme = window.__PUSHER_SCHEME__ || process.env.VUE_APP_PUSHER_SCHEME || 'https'
      const pusherCluster = window.__PUSHER_CLUSTER__ || process.env.VUE_APP_PUSHER_APP_CLUSTER || 'mt1'

      this.pusher = new Pusher(pusherKey, {
        wsHost: pusherHost,
        wsPort: pusherPort,
        wssPort: pusherPort,
        forceTLS: pusherScheme === 'https',
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        cluster: pusherCluster,
        authEndpoint: API_BASE_URL + '/api/broadcasting/auth',
        auth: {
          headers: {
            'Authorization': 'Bearer ' + token,
            'X-Wallet-Address': walletAddress,
            'Accept': 'application/json'
          }
        }
      })

      const channelName = 'private-customer-service.user.' + this.userId
      this.channel = this.pusher.subscribe(channelName)

      this.channel.bind('new.cs.message', (data) => {
        // 收到新消息
        const msg = data.message
        // 避免添加重复消息
        if (this.messages.some(m => m.id === msg.id)) return

        this.messages.push({
          id: msg.id,
          role: msg.sender_type === 'user' ? 'user' : 'support',
          type: msg.type || 'text',
          text: msg.type === 'image' ? '' : msg.content,
          imageUrl: msg.type === 'image' ? msg.content : '',
          time: new Date(msg.created_at).getTime()
        })
        this.lastMessageId = msg.id
        this.$nextTick(this.scrollToBottom)
      })

      this.channel.bind('pusher:subscription_error', (status) => {
        console.error('客服 WebSocket 订阅失败:', status)
      })
    },
    /** 断开 WebSocket */
    disconnectSocket() {
      if (this.channel) {
        this.channel.unbind_all()
        this.channel = null
      }
      if (this.pusher) {
        this.pusher.disconnect()
        this.pusher = null
      }
    },
    scrollToBottom() {
      const el = this.$refs.messageListRef
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(ts) {
      const d = new Date(ts)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.customer-service-wrapper {
  z-index: 9999;
}

.customer-service-fab {
  position: fixed;
  right: 16px;
  bottom: calc(58px + 16px + var(--app-safe-bottom, env(safe-area-inset-bottom, 0px)));
  width: 48px;
  height: 48px;
  border: 1px solid #2eb4d4;
  border-radius: 50%;
  background: #2eb4d4;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(47, 126, 168, 0.14);
}

.customer-service-fab .fab-icon {
  width: 22px;
  height: 22px;
  display: block;
}

.fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.5);
  animation: badge-pulse 2s ease-in-out infinite;
}

@keyframes badge-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.customer-service-panel {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(360px, calc(100% - 24px), calc(var(--mobile-shell-width, 430px) - 24px));
  max-width: calc(var(--mobile-shell-width, 430px) - 24px);
  height: min(560px, calc(100vh - 120px));
  background: #fff;
  border: 1px solid #d7eef3;
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(22, 52, 61, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 10000;
}

.panel-header {
  height: 72px;
  padding: 14px;
  background: #2eb4d4;
  color: #fff;
  border-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-logo {
  width: 72px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 0;
  object-fit: contain;
  object-position: left center;
  display: block;
}

.title {
  font-size: 20px;
  line-height: 1;
  font-weight: 700;
}

.status {
  margin-top: 6px;
  color: rgba(255,255,255,0.82);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.online-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.16);
  color: #fff;
  cursor: pointer;
  line-height: 1;
}

.icon-btn.close {
  font-size: 18px;
}

.panel-body {
  flex: 1;
  padding: 14px;
  overflow-y: auto;
}

.message-row {
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.message-row.support {
  align-items: flex-start;
}

.message-row.user {
  align-items: flex-end;
}

.bubble {
  max-width: 85%;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-wrap;
}

.bubble.image {
  padding: 6px;
}

.chat-image {
  display: block;
  max-width: 180px;
  max-height: 180px;
  border-radius: 10px;
}

.message-row.support .bubble {
  background: #e7f6f8;
  color: #16343d;
}

.message-row.user .bubble {
  background: #2eb4d4;
  color: #fff;
}

.time {
  margin-top: 4px;
  font-size: 11px;
  color: #7d8f84;
}

.panel-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #d7eef3;
  background: #fff;
}

.upload-input {
  display: none;
}

.upload-btn {
  width: 42px;
  height: 42px;
  border: 0;
  background: #d7eef3;
  color: #16343d;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.message-input {
  flex: 1;
  height: 42px;
  border: 0;
  background: #e7f6f8;
  color: #16343d;
  padding: 0 12px;
  font-size: 14px;
}

.message-input::placeholder {
  color: #66786f;
}

.send-btn {
  height: 42px;
  min-width: 74px;
  border: none;
  cursor: pointer;
  background: #2eb4d4;
  color: #fff;
  font-weight: 600;
}

@media (max-width: 480px) {
  .title {
    font-size: 18px;
  }

  .customer-service-panel {
    width: calc(100vw - 20px);
    max-width: calc(100vw - 20px);
    height: min(600px, calc(100vh - 90px));
    border-radius: 14px;
  }
}

@media (min-width: 431px) {
  .customer-service-panel {
    /* Align to the centered H5 shell, not the full desktop viewport */
    left: 50%;
    width: min(360px, calc(var(--mobile-shell-width, 430px) - 24px));
    max-width: calc(var(--mobile-shell-width, 430px) - 24px);
  }
}
</style>
